/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useRef, useState } from "react";
import cn from "classnames";
import PropTypes from "prop-types";
import useImageWrapper from "helpers/hooks/useImageWrapper";
import { Marker } from "react-map-gl";
import { usePopper } from "react-popper";
import { moneyWithoutDecimals } from "helpers/formatting";
import useDetectOutsideClick from "helpers/hooks/useDetectOutsideClick";
import Link from "next/link";
import Photo from "components/shared/stays/Photo";
import Text from "components/shared/stays/Text";
import styles from "components/shared/stays/MapCardLarge.module.scss";

export default function MapCardLarge({
  latitude,
  longitude,
  name,
  slug,
  lowestPrice,
  lowestProductPricingType,
  imageSrc,
  country,
}) {
  const { getImageWidth } = useImageWrapper();
  const [imgWidths, setImgWidths] = useState([0]);
  const imgWrapperRef = useRef(null);
  const pointRef = useRef(null);
  const popupRef = useRef(null);

  const [offsets, setOffsets] = useState({
    top: 0,
    left: 0,
  });

  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const calculatedWidth = getImageWidth([imgWrapperRef]);
    setImgWidths(calculatedWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDetectOutsideClick(pointRef, popupRef, () => setShowPopup(false));

  const { attributes, update } = usePopper(pointRef.current, popupRef.current, {
    placement: "auto",
    modifiers: [
      {
        name: "preventOverflow",
        options: {
          padding: 50,
        },
      },
    ],
  });

  const formattedPrice = moneyWithoutDecimals(lowestPrice);

  useEffect(() => {
    const pointWidth = pointRef.current?.offsetWidth;
    const pointHeight = pointRef.current?.offsetHeight;
    setOffsets({
      top: -(pointHeight / 2 || 0),
      left: -(pointWidth / 2 || 0),
    });
  }, []);

  return (
    <Marker
      latitude={latitude}
      longitude={longitude}
      offsetLeft={offsets.left}
      offsetTop={offsets.top}
      style={{ zIndex: showPopup ? 3 : 1 }}
    >
      <div className={cn(styles.component, showPopup && styles.open)}>
        <button
          type="button"
          className={styles.point}
          onClick={async () => {
            await update();
            setShowPopup(!showPopup);
          }}
          ref={pointRef}
        >
          {formattedPrice}
        </button>

        <div
          className={cn(styles.popup, showPopup && styles.open)}
          ref={popupRef}
          {...attributes.popper}
          aria-hidden={showPopup ? "false" : "true"}
        >
          <Link
            prefetch={false}
            href={`/stays/${slug}`}
            className={styles.link}
            target="_blank"
          >
            <div className={styles.photo} ref={imgWrapperRef}>
              <Photo
                src={imageSrc}
                alt={name}
                layout="fill"
                objectFit="cover"
                placeholder
                containerWidth={imgWidths[0]}
              />
            </div>
            <div className={styles.description}>
              <Text className={styles.country} size={3}>
                {country}
              </Text>
              <Text className={styles.name}>{name}</Text>
              <Text size={2}>
                {formattedPrice}
                {` / ${
                  lowestProductPricingType?.includes("day") ? "day" : "night"
                }`}
              </Text>
            </div>
          </Link>
        </div>
      </div>
    </Marker>
  );
}

MapCardLarge.propTypes = {
  latitude: PropTypes.number.isRequired,
  longitude: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  lowestPrice: PropTypes.node.isRequired,
  lowestProductPricingType: PropTypes.string.isRequired,
  imageSrc: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
};
